import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart3, Download, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { generateReportPDF } from '../lib/pdf';

const CAT_COLORS = ['#0f766e', '#0891b2', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981'];

export default function Analytics() {
  const { user } = useContext(AuthContext);
  const [analytics, setAnalytics] = useState(null);
  const [utilityAnalytics, setUtilityAnalytics] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [months, setMonths] = useState(6);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => { fetchData(); }, [months]);
  useEffect(() => { if (selectedBuilding) fetchUtility(); }, [selectedBuilding]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        axios.get(`${API}/analytics/complaints?months=${months}`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setAnalytics(a.data); setBuildings(b.data);
      if (b.data.length && !selectedBuilding) setSelectedBuilding(b.data[0].id);
      setCompany(c.data.find(x => x.id === user?.company_id) || c.data[0]);
    } catch { toast.error('Failed to load analytics'); }
    setLoading(false);
  };

  const fetchUtility = async () => {
    setAiLoading(true);
    try {
      const r = await axios.get(`${API}/analytics/utility/${selectedBuilding}`, { withCredentials: true });
      setUtilityAnalytics(r.data);
    } catch { /* no readings yet */ }
    setAiLoading(false);
  };

  const downloadPDF = () => {
    if (!analytics) return;
    generateReportPDF({
      title: 'FM Operations Analytics',
      subtitle: `${months}-month complaints & utility review`,
      company,
      sections: [
        {
          heading: 'Complaints Month-on-Month',
          head: ['Month', 'Total', 'Resolved', 'Resolution %'],
          rows: analytics.monthly.map(m => [m.label, m.total, m.resolved, `${m.resolution_rate}%`])
        },
        {
          heading: 'Complaints by Category (Current Month)',
          head: ['Category', 'Count'],
          rows: (analytics.by_category_current_month || []).map(c => [c.category, c.count])
        },
        ...(utilityAnalytics?.insights?.length ? [{
          heading: `Utility Trends - ${utilityAnalytics.building.name}`,
          head: ['Type', 'Last', 'Prev Avg', 'Change %', 'Status'],
          rows: utilityAnalytics.insights.map(i => [i.utility_type, i.last_value, i.previous_avg, `${i.change_pct}%`, i.status])
        }] : []),
        ...(utilityAnalytics?.ai_remark ? [{
          heading: 'AI Recommendations',
          paragraph: utilityAnalytics.ai_remark
        }] : [])
      ]
    });
  };

  if (loading) return <Layout><div className="py-12 text-center text-muted-foreground">Loading analytics…</div></Layout>;

  const byCat = (analytics?.by_category_current_month || []).map((c, i) => ({ ...c, fill: CAT_COLORS[i % CAT_COLORS.length] }));

  return (
    <Layout>
      <div data-testid="analytics-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2"><BarChart3 className="w-6 h-6 text-teal-600" />Analytics</h1>
            <p className="text-sm text-muted-foreground">Month-on-month trends with AI-powered utility insights</p>
          </div>
          <div className="flex gap-2">
            <Select value={String(months)} onValueChange={(v) => setMonths(parseInt(v))}>
              <SelectTrigger data-testid="months-select" className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 months</SelectItem>
                <SelectItem value="6">6 months</SelectItem>
                <SelectItem value="12">12 months</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="download-analytics-pdf" onClick={downloadPDF} variant="outline"><Download className="w-4 h-4 mr-1" />PDF</Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <h3 className="font-semibold text-foreground mb-4">Complaints — Month on Month</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics?.monthly || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill="#0891b2" name="Total" radius={[6, 6, 0, 0]} />
                <Bar dataKey="resolved" fill="#0f766e" name="Resolved" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-foreground mb-4">Resolution Rate (%)</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={analytics?.monthly || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                <Tooltip formatter={(v) => [`${v}%`, 'Resolution Rate']} />
                <Line type="monotone" dataKey="resolution_rate" stroke="#0f766e" strokeWidth={2} dot={{ fill: '#0f766e', r: 4 }} name="Resolution %" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <h3 className="font-semibold text-foreground mb-4">By Category (Current Month)</h3>
            {byCat.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={byCat} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={90} label>
                    {byCat.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-10">No complaints this month</p>}
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-foreground">Utility Trends (per Building)</h3>
              <Select value={selectedBuilding} onValueChange={setSelectedBuilding}>
                <SelectTrigger data-testid="util-building-select" className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>{buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {aiLoading && <p className="text-xs text-muted-foreground">Analyzing...</p>}
            {utilityAnalytics?.insights?.length > 0 ? (
              <div className="space-y-2">
                {utilityAnalytics.insights.map((i, idx) => (
                  <div key={idx} data-testid={`util-insight-${idx}`} className={`p-3 rounded-lg flex items-center justify-between ${i.status === 'significant increase' ? 'bg-red-50' : i.status === 'slight increase' ? 'bg-amber-50' : 'bg-slate-50'}`}>
                    <div>
                      <div className="font-semibold text-sm capitalize text-slate-900">{i.utility_type}</div>
                      <div className="text-xs text-slate-600">Last: {i.last_value} · Prev avg: {i.previous_avg}</div>
                    </div>
                    <div className={`flex items-center gap-1 font-semibold text-sm ${i.change_pct > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {i.change_pct > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {i.change_pct > 0 ? '+' : ''}{i.change_pct}%
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">No utility readings to analyze yet.</p>}
          </Card>
        </div>

        {utilityAnalytics?.ai_remark && (
          <Card className="p-5 border-l-4 border-l-purple-500 bg-purple-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-purple-700">✨ AI Recommendation</span>
            </div>
            <p data-testid="utility-ai-remark" className="text-sm text-slate-700 whitespace-pre-wrap">{utilityAnalytics.ai_remark}</p>
          </Card>
        )}
      </div>
    </Layout>
  );
}
